import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { type AppLocale, dirFor, INTL_LOCALE } from '@hk/core'

/* LocaleProvider — one place that knows the language, and therefore the
   direction, the font and the numbering system. Components never take a
   `isRtl` prop: they read direction from here, or better, they use CSS logical
   properties and do not need to know at all. */

interface LocaleContextValue {
  locale: AppLocale
  dir: 'ltr' | 'rtl'
  intlLocale: string
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'en', dir: 'ltr', intlLocale: INTL_LOCALE.en,
})

export function LocaleProvider(
  { locale, children }: { locale: AppLocale; children: ReactNode },
) {
  const value = useMemo(
    () => ({ locale, dir: dirFor(locale), intlLocale: INTL_LOCALE[locale] }),
    [locale],
  )
  return (
    <LocaleContext value={value}>
      <div lang={locale} dir={value.dir} className="contents">{children}</div>
    </LocaleContext>
  )
}

export const useLocale = () => useContext(LocaleContext)
